import type { FunctionComponent } from "react";
import React, { useEffect, useState } from "react";

import styled from "styled-components";

import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { MmxTrafficSource } from "app/pages/website-analysis/traffic-sources/mmx/components/common/types";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { PoPCompareChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPCompareChart/PoPCompareChart";
import { PoPSingleChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/PoPSingleChart";
import {
    StyledBox,
    TitleContainer,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/StyledComponents";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/mmx/components/common/EmptyState";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { dataModeMap, percentDataFormat } from "../ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { useDataFormat } from "../ChannelsOverviewBarChart/ChannelsOverviewBarChartSwitcher";
import { EViewBySwitcherItems } from "../ChannelsOverviewBarChart/components/view-by-switcher/ViewBySwitcher";
import { TotalTrafficSourceDisplayMap } from "../ChannelsOverviewBarChart/types";

import { convertChartRawDataToDomainPopData } from "./PoPCompareChart/view-by-domain-pop-chart/utils";
import ViewByDomainPopChart from "./PoPCompareChart/view-by-domain-pop-chart/ViewByDomainPopChart";
import TitleActions from "./TitleActions"; // Import the new component
import type { IChartRawData } from "./types";

const LoaderWrapper = styled(CenteredFlexRow)`
    min-height: 410px;
`;

const { Channels, Domain } = EViewBySwitcherItems;

export const ChannelsOverviewPoPChartContainer: FunctionComponent<any> = () => {
    const params = proNavigator.getParams();
    const fetchService = DefaultFetchService.getInstance();
    const chartRef = React.createRef<HTMLDivElement>();
    const { country, duration, comparedDuration, webSource, isWWW, key } = params;
    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const { from, to, isWindow, compareFrom, compareTo } = durationObject.forAPI;
    const durations = [durationObject.forWidget[1], durationObject.forWidget[0]];
    const chosenSites = chosenSitesService.get() as any;
    const getSiteColor = chosenSitesService.getSiteColor;
    const [viewBy, setViewBy] = useState<number>(() => {
        const { channelsOverviewViewByType } = proNavigator.getParams();
        return channelsOverviewViewByType === EViewBySwitcherItems[Domain].toString() ? Domain : Channels;
    });
    const isCompare = key.split(",").length > 1;
    const isTotalWeb = webSource === devicesTypes.TOTAL;
    const [dataFormat, setDataFormat] = useDataFormat(isCompare);
    const mmxEndpoint = isTotalWeb
        ? "widgetApi/MarketingMixTotal/TrafficSourcesOverview/PieChartPop"
        : "widgetApi/MarketingMix/TrafficSourcesOverview/PieChart";

    const transformData = (data) => {
        const extractData = (rawData) => (isTotalWeb ? rawData.Total : rawData);
        const dataObj = {
            Data: { ...extractData(data.Data) },
            ComparedData: { ...extractData(data.ComparedData.Data) },
        };
        return { ...dataObj };
    };

    const apiParams = {
        country,
        from,
        includeSubDomains: isWWW === "*",
        isWindow,
        keys: key,
        timeGranularity: "Monthly",
        to,
        webSource,
        compareTo,
        compareFrom,
    };

    const { data, isLoading, isError, error } = useQuery<IChartRawData>({
        queryKey: ["channelsOverviewPoP", mmxEndpoint, apiParams],
        queryFn: async ({ signal }) => {
            const response = await fetchService.get<{ Data: object }>(mmxEndpoint, apiParams, {
                cancellation: signal,
            });
            return transformData(response);
        },
    });

    useEffect(() => {
        if (error) {
            Sentry.error("error fetching MMXPoPChartContainer data", error);
        }
    }, [error]);

    const viewBySwitcherClick = (selectedViewBy: EViewBySwitcherItems) => {
        setViewBy(selectedViewBy);
    };

    const hasAnyValue = <T extends object>(obj?: Record<string | number, T | undefined> | null): boolean => {
        return Object.values(obj ?? {}).some(
            (inner) => inner != null && Object.values(inner).some((v): v is number => typeof v === "number" && v > 0),
        );
    };

    const isDataExists = () => {
        if (!data) return false;

        if (isCompare) {
            return hasAnyValue(data.Data) || hasAnyValue(data.ComparedData);
        }

        return hasAnyValue({ [key]: data.Data?.[key] }) && hasAnyValue({ [key]: data.ComparedData?.[key] });
    };

    const getComponent = () =>
        isCompare ? (
            viewBy === EViewBySwitcherItems.Channels ? (
                <PoPCompareChart
                    globalParams={{ compareFrom: compareFrom, from: from, params: params }}
                    chartRawData={data}
                    chosenSites={chosenSites}
                    getSiteColor={getSiteColor}
                    durations={durations}
                    isPercentageDataMode={dataModeMap[dataFormat] === dataModeMap[percentDataFormat]}
                />
            ) : (
                <ViewByDomainPopChart
                    chartRawData={convertChartRawDataToDomainPopData(
                        data,
                        isTotalWeb ? Object.values(MmxTrafficSource) : undefined,
                    )}
                    dataFormat={dataFormat}
                    durations={durations}
                    trafficSourcesMap={isTotalWeb ? TotalTrafficSourceDisplayMap : undefined}
                />
            )
        ) : (
            <PoPSingleChart
                globalParams={{ compareFrom: compareFrom, from: from, params: params }}
                chartRawData={data}
                legendDurations={durations}
                options={{ categoryXSeries: true, height: 190 }}
                webSource={webSource}
                isPercentageDataMode={dataModeMap[dataFormat] === dataModeMap[percentDataFormat]}
            />
        );

    return (
        <StyledBox
            width="100%"
            height="fit-content"
            ref={chartRef}
        >
            {isLoading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                <>
                    <TitleContainer>
                        <TitleActions
                            isCompare={isCompare}
                            viewBy={viewBy}
                            onViewChange={viewBySwitcherClick}
                            onFormatChange={setDataFormat}
                            chartRef={chartRef}
                            dataFormat={dataFormat}
                        />
                    </TitleContainer>
                    {isError ? (
                        <EmptyState
                            iconName="error-state"
                            titleKey="common.error"
                            subTitleKey="common.error.subtitle"
                        />
                    ) : isDataExists() ? (
                        getComponent()
                    ) : (
                        <EmptyState
                            titleKey="mmx.channelsoverview.bar.chart.no.data.title"
                            subTitleKey="mmx.channelsoverview.bar.chart.no.data.subtitle"
                        />
                    )}
                </>
            )}
        </StyledBox>
    );
};
